import {useCallback, useState} from "react";
import type Konva from "konva";
import type {GridPoint, LevelGeometryDocument} from "@/dungeons/model/DungeonGeometry.ts";
import {gridToImage, imageToGrid} from "@/dungeons/geometry/Coordinates.ts";
import {
    snapGeometryPoint,
    type ExcludedGeometrySnapTarget,
    type GeometrySnapResult,
} from "./GeometrySnapping.ts";

type DragState = {key: string; snap: GeometrySnapResult};
type DragBinding = {
    key: string;
    excluded: ExcludedGeometrySnapTarget;
    onCommit: (point: GridPoint) => void;
    onPreview?: (point: GridPoint | undefined) => void;
};

export default function useSnappedGeometryDrag({geometry, zoom, snappingEnabled}: {
    geometry: LevelGeometryDocument;
    zoom: number;
    snappingEnabled: boolean;
}) {
    const [dragged, setDragged] = useState<DragState>();
    const resolve = useCallback((point: GridPoint, controlKey: boolean, excluded: ExcludedGeometrySnapTarget) =>
        snapGeometryPoint({
            point: imageToGrid(point, geometry.registration),
            document: geometry,
            pixelsPerCell: zoom * geometry.registration.pixelsPerCell,
            enabled: snappingEnabled && !controlKey,
            excluded,
        }), [geometry, snappingEnabled, zoom]);
    const bind = useCallback(({key, excluded, onCommit, onPreview}: DragBinding) => {
        const update = (event: Konva.KonvaEventObject<DragEvent>) => {
            const snap = resolve({x: event.target.x(), y: event.target.y()}, event.evt.ctrlKey, excluded);
            event.target.position(gridToImage(snap.point, geometry.registration));
            setDragged({key, snap});
            onPreview?.(snap.point);
            return snap;
        };
        return {
            onDragStart: update,
            onDragMove: update,
            onDragEnd: (event: Konva.KonvaEventObject<DragEvent>) => {
                const snap = update(event);
                onCommit(snap.point);
                onPreview?.(undefined);
                setDragged(undefined);
            },
        };
    }, [geometry.registration, resolve]);
    const pointFor = useCallback((key: string, fallback: GridPoint) =>
        dragged?.key === key ? dragged.snap.point : fallback, [dragged]);

    return {dragged, bind, pointFor};
}
